import { useState, useEffect } from "react";
import { api } from "../api";
import type { AssetRow, FieldDef, Identity } from "../types";
import Icon from "../components/Icon";
import Badge from "../components/Badge";

type CatalogProps = {
  clientId: string;
  identity: Identity;
  onOpenAsset: (id: string) => void;
  onNewAsset: () => void;
};

function pubStateToBadge(s: string | null): "published" | "draft" | "archived" {
  if (s === "published") return "published";
  if (s === "archived") return "archived";
  return "draft";
}

export default function Catalog({ clientId, identity, onOpenAsset, onNewAsset }: CatalogProps) {
  const canCreate = identity.role === "platform_owner";
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([api.getFieldDefs(clientId), api.getAssets(clientId, { include_deleted: showDeleted })])
      .then(([fdRes, aRes]) => {
        setFieldDefs(fdRes.field_defs);
        setAssets(aRes.assets);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [clientId, showDeleted]);

  const listCols = fieldDefs.filter((fd) => fd.show_in_list === 1).slice(0, 5);

  const filtered = assets.filter((a) => {
    if (showDeleted) {
      // deleted view: only show soft-deleted, apply search
      if (!a.deleted_at) return false;
      return !query || a.title.toLowerCase().includes(query.toLowerCase());
    }
    const norm = a.publication_state ?? "draft";
    const matchState = stateFilter === null || norm === stateFilter;
    const matchQ = !query || a.title.toLowerCase().includes(query.toLowerCase());
    return matchState && matchQ;
  });

  function countState(s: string) {
    return assets.filter((a) => (a.publication_state ?? "draft") === s).length;
  }

  return (
    <div className="page page-enter">
      <div style={{ maxWidth: 1480, margin: "0 auto" }}>
        <div className="section-head">
          <div>
            <span className="eyebrow">Catalog · {assets.length} {assets.length === 1 ? "asset" : "assets"}</span>
            <h2 className="t-h2" style={{ marginTop: 10, fontSize: 32 }}>The catalog.</h2>
          </div>
          <div className="actions">
            {canCreate && (
              <button className="btn btn-primary btn-sm" onClick={onNewAsset}>
                <Icon name="plus" size={14} /> New asset
              </button>
            )}
          </div>
        </div>

        <div className="filter-row">
          <input
            className="input"
            placeholder="Search assets…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ minWidth: 320, flex: "0 1 320px" }}
          />
          {showDeleted ? (
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-muted)" }}>
              Showing soft-deleted assets
            </span>
          ) : (
            <div className="chip-filter">
              <button
                className={stateFilter === null ? "on" : ""}
                onClick={() => setStateFilter(null)}
              >
                All <span className="count">{assets.filter((a) => !a.deleted_at).length}</span>
              </button>
              <button
                className={stateFilter === "draft" ? "on" : ""}
                onClick={() => setStateFilter("draft")}
              >
                Draft <span className="count">{countState("draft")}</span>
              </button>
              <button
                className={stateFilter === "published" ? "on" : ""}
                onClick={() => setStateFilter("published")}
              >
                Published <span className="count">{countState("published")}</span>
              </button>
              <button
                className={stateFilter === "archived" ? "on" : ""}
                onClick={() => setStateFilter("archived")}
              >
                Archived <span className="count">{countState("archived")}</span>
              </button>
            </div>
          )}
          <div className="spacer" />
          <button
            className={`btn btn-sm ${showDeleted ? "btn-quiet" : "btn-quiet"}`}
            onClick={() => {
              setShowDeleted((v) => !v);
              setStateFilter(null);
              setQuery("");
            }}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: showDeleted ? "var(--accent)" : "var(--fg-muted)",
            }}
          >
            <Icon name="trash" size={12} />
            {showDeleted ? "Hide deleted" : "Deleted"}
          </button>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--fg-subtle)", marginLeft: 4 }}>
            Sort by modified
          </span>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(229,138,109,0.10)",
              border: "1px solid rgba(229,138,109,0.35)",
              borderRadius: "var(--r-md)",
              color: "#e58a6d",
              marginBottom: 20,
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Icon name="x" size={14} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td>
                      <div className="skeleton" style={{ height: 14, width: "55%", borderRadius: 3 }} />
                    </td>
                    <td>
                      <div className="skeleton" style={{ height: 14, width: "35%", borderRadius: 3 }} />
                    </td>
                    <td>
                      <div className="skeleton" style={{ height: 18, width: 64, borderRadius: 4 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty card" style={{ padding: "80px 24px" }}>
            <div className="glyph">
              <Icon name="list" size={22} />
            </div>
            {query || stateFilter ? (
              <>
                <h4 className="t-h4">No matching assets.</h4>
                <p style={{ marginTop: 8 }}>Try clearing the filter or search term.</p>
                <button
                  className="btn btn-quiet btn-sm"
                  style={{ marginTop: 18 }}
                  onClick={() => {
                    setQuery("");
                    setStateFilter(null);
                  }}
                >
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <h4 className="t-h4">No assets yet.</h4>
                {canCreate ? (
                  <>
                    <p style={{ marginTop: 8 }}>Create the first asset for this client.</p>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: 18 }}
                      onClick={onNewAsset}
                    >
                      <Icon name="plus" size={14} /> New asset
                    </button>
                  </>
                ) : (
                  <p style={{ marginTop: 8 }}>Mark hasn&apos;t added any assets to your catalog yet.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  {listCols.map((fd) => (
                    <th key={fd.field_name}>{fd.display_name}</th>
                  ))}
                  <th>Slug</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} onClick={() => onOpenAsset(a.id)}>
                    <td className="col-title">{a.title}</td>
                    {listCols.map((fd) => (
                      <td key={fd.field_name}>{a.fields?.[fd.field_name] ?? "—"}</td>
                    ))}
                    <td className="col-slug">{a.slug ?? "—"}</td>
                    <td>
                      <Badge kind={pubStateToBadge(a.publication_state)}>
                        {a.publication_state ?? "draft"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
